import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../context/ConfirmContext';
import {
  PlusIcon,
  MinusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  SparklesIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import Modal from '../components/Modal';
import { AppIconButton } from '../components/ui';
import PillTabBar from '../components/ui/PillTabBar';
import CollapsibleFilters from '../components/ui/CollapsibleFilters';
import PlannerTab from '../components/inventory/PlannerTab';
import DailySalesTab from '../components/inventory/DailySalesTab';
import InventoryOverviewTab from '../components/inventory/InventoryOverviewTab';
import InventoryInsightsTab from '../components/inventory/InventoryInsightsTab';
import VarianceReportTab from '../components/inventory/VarianceReportTab';
import StockCountPrintSheet from '../components/inventory/StockCountPrintSheet';
import WeeklySheetsTab from '../components/inventory/WeeklySheetsTab';
import PrepRecipeDialog from '../components/inventory/PrepRecipeDialog';
import ToggleSwitch from '../components/ToggleSwitch';
import { useVendorConfig } from '../context/VendorConfigContext';
import { inventoryApi, sheetsApi } from '../api/inventoryApi';
import api from '../api/api';
import { formatCurrency } from '../config/currency';
import { scaleRecipeMappings } from '../utils/recipeScaling';
import { formatQuantity } from '../utils/formatQuantity';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  Autocomplete,
  InputAdornment,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Divider,
  Button,
  Card,
  CardContent,
  CircularProgress,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';

// --- Interfaces ---

interface InventoryItem {
  id: number;
  name: string;
  unit: string;
  currentStock: number;
  lowStockThreshold: number;
  isActive: boolean;
  isLowStock: boolean;
  /** prepared in the kitchen from other inventory items (has a prep recipe) */
  isPrepared?: boolean;
  /** optional grouping used for weekly stock sheets (e.g. "Kitchen", "Bar") */
  category?: string | null;
}

interface RecipeMapping {
  id: number;
  inventoryItemId: number;
  inventoryItemName: string;
  unit: string;
  portionId?: number | null;
  portionName?: string | null;
  quantityRequired: number;
  batchQuantity?: number | null;
  batchYield?: number | null;
}

interface PortionOption {
  id: number;
  portionName: string;
}

interface SuggestedIngredient {
  name: string;
  quantityPerPortion: number;
  unit: string;
  notes?: string | null;
  matched: boolean;
  inventoryItemId?: number | null;
  inventoryItemName?: string | null;
  inventoryItemUnit?: string | null;
  /** quantity converted into the matched item's unit; null = unit mismatch, verify manually */
  convertedQuantity?: number | null;
}

interface RecipeSuggestion {
  menuItemName: string;
  region: string;
  model: string;
  /** What one served portion of the finished dish is, e.g. "one plate ≈ 450 g cooked biryani" */
  servingDescription?: string | null;
  servingWeightGrams?: number | null;
  ingredients: SuggestedIngredient[];
}

interface PortionCost {
  portionId: number;
  portionName: string;
  sellingPrice: number | null;
  foodCost: number;
  marginPercent: number | null;
  usesBaseRecipe: boolean;
}

interface FoodCostSummary {
  menuItemId: number;
  menuItemName: string;
  sellingPrice: number | null;
  foodCost: number;
  marginPercent: number | null;
  portions: PortionCost[];
}

interface StockAuditLog {
  id: number;
  inventoryItemId: number;
  inventoryItemName: string;
  changeType: string;
  quantityChange: number;
  stockBefore: number;
  stockAfter: number;
  orderId: number | null;
  notes: string | null;
  performedBy: string;
  /** business date of the movement (backdatable) */
  occurredOn?: string;
  createdAt: string;
}

interface MenuItemOption {
  id: number;
  name: string;
  categoryName?: string;
  portions: PortionOption[];
}

interface StockReport {
  totalItems: number;
  lowStockCount: number;
  outOfStockCount: number;
}

// --- Constants ---

const UNITS = [
  { value: 'KG', label: 'Kilograms' },
  { value: 'GRAMS', label: 'Grams' },
  { value: 'LITRE', label: 'Litres' },
  { value: 'ML', label: 'Millilitres' },
  { value: 'PIECES', label: 'Pieces' },
  { value: 'UNITS', label: 'Units' },
];

// Stock-out reasons for the quick minus dialog — each maps to an audit
// change type so wastage vs usage stays separable in reports.
const STOCK_OUT_REASONS = [
  { value: 'WASTAGE', label: 'Wastage' },
  { value: 'KITCHEN_USE', label: 'Kitchen use' },
  { value: 'MANUAL_ADJUSTMENT', label: 'Correction' },
  { value: 'RETURNED_TO_SUPPLIER', label: 'Returned to supplier' },
];

// Sub-unit mapping: when an ingredient is stored in a base unit (KG/LITRE),
// recipe quantities are entered in sub-units (grams/ml) for convenience.
const SUB_UNIT_MAP: Record<string, { subUnit: string; label: string; factor: number }> = {
  KG: { subUnit: 'grams', label: 'Grams', factor: 1000 },      // 1 KG = 1000 grams
  LITRE: { subUnit: 'ml', label: 'Millilitres', factor: 1000 }, // 1 LITRE = 1000 ml
};

// Get the display label and conversion for recipe quantity input
const getRecipeUnitInfo = (baseUnit: string) => {
  const sub = SUB_UNIT_MAP[baseUnit];
  if (sub) return { label: sub.label, factor: sub.factor };
  // For GRAMS, ML, PIECES, UNITS — use as-is
  const u = UNITS.find((u) => u.value === baseUnit);
  return { label: u?.label || baseUnit, factor: 1 };
};

// CHANGE_TYPE_COLORS moved inside component for theme support

// --- Component ---

const InventoryManagement: React.FC = () => {
  const { theme } = useTheme();
  const { user, isSteward } = useAuth();
  const { configuration, updateConfiguration, branding } = useVendorConfig();
  const confirm = useConfirm();
  // Standalone mode: no POS — daily sales are reported manually
  const standaloneMode = configuration?.inventoryPosIntegrated === false;
  const [configSaving, setConfigSaving] = useState(false);
  // Set when jumping from a Daily Sales day card's "View variance" link
  const [varianceRange, setVarianceRange] = useState<{ from: string; to: string } | null>(null);

  const handleToggleInventorySetting = async (key: 'inventoryPosIntegrated' | 'inventoryDeductOnManualSales', currentValue: boolean) => {
    try {
      setConfigSaving(true);
      await updateConfiguration({ [key]: !currentValue });
      // Daily Sales tab disappears when switching back to POS mode
      if (key === 'inventoryPosIntegrated' && !currentValue === true) {
        setActiveTab((prev) => (prev === 'dailySales' ? 'settings' : prev));
      }
    } catch {
      setError('Failed to update inventory settings');
    } finally {
      setConfigSaving(false);
    }
  };
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));

  // Theme-aware change type colors
  const CHANGE_TYPE_COLORS: Record<string, { bg: string; color: string }> = {
    ORDER_DEDUCTION: { bg: (theme.components?.status?.preparing?.text || '#2563eb') + '20', color: theme.components?.status?.preparing?.text || '#2563eb' },
    RESTOCK: { bg: (theme.success || '#059669') + '20', color: theme.success || '#059669' },
    WASTAGE: { bg: (theme.danger || '#dc2626') + '20', color: theme.danger || '#dc2626' },
    MANUAL_ADJUSTMENT: { bg: (theme.warning || '#d97706') + '20', color: theme.warning || '#d97706' },
    INITIAL_STOCK: { bg: (theme.gray[500] || '#4b5563') + '20', color: theme.gray[500] || '#4b5563' },
    MANUAL_SALE: { bg: (theme.components?.status?.preparing?.text || '#2563eb') + '20', color: theme.components?.status?.preparing?.text || '#2563eb' },
    MANUAL_SALE_REVERSAL: { bg: (theme.warning || '#d97706') + '20', color: theme.warning || '#d97706' },
    PREP_PRODUCTION: { bg: (theme.success || '#059669') + '20', color: theme.success || '#059669' },
    PREP_CONSUMPTION: { bg: (theme.components?.status?.preparing?.text || '#2563eb') + '20', color: theme.components?.status?.preparing?.text || '#2563eb' },
    KITCHEN_USE: { bg: (theme.components?.status?.preparing?.text || '#2563eb') + '20', color: theme.components?.status?.preparing?.text || '#2563eb' },
    RETURNED_TO_SUPPLIER: { bg: (theme.warning || '#d97706') + '20', color: theme.warning || '#d97706' },
  };

  // Tab state (value-based so the conditional Daily Sales tab can sit
  // mid-list while Settings stays last)
  const [activeTab, setActiveTab] = useState('overview');

  // Inventory items state
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [report, setReport] = useState<StockReport>({ totalItems: 0, lowStockCount: 0, outOfStockCount: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal states
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [prepItem, setPrepItem] = useState<InventoryItem | null>(null);
  // Quick stock in/out (+/- on item rows): backdatable, reason-tagged
  const [quickAdjust, setQuickAdjust] = useState<{ item: InventoryItem; mode: 'in' | 'out' } | null>(null);
  const [quickForm, setQuickForm] = useState({ quantity: 0, occurredOn: '', reason: 'WASTAGE', notes: '' });

  // Item form
  const [itemForm, setItemForm] = useState({ name: '', unit: 'PIECES', currentStock: 0, lowStockThreshold: 5, category: '' });
  // Known inventory categories — for the item form's freeSolo autocomplete
  const [itemCategories, setItemCategories] = useState<string[]>([]);


  // Recipe tab state
  const [menuItems, setMenuItems] = useState<MenuItemOption[]>([]);
  const [selectedMenuItemId, setSelectedMenuItemId] = useState<number | ''>('');
  const [recipeMappings, setRecipeMappings] = useState<RecipeMapping[]>([]);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [newIngredientId, setNewIngredientId] = useState<number | ''>('');
  const [newIngredientQty, setNewIngredientQty] = useState<number>(0);
  const [recipeSaving, setRecipeSaving] = useState(false);
  // '' = base recipe scope; number = portion-specific scope
  const [recipePortionScope, setRecipePortionScope] = useState<number | ''>('');
  const [entryMode, setEntryMode] = useState<'portion' | 'batch'>('portion');
  const [newBatchQty, setNewBatchQty] = useState<number>(0);
  const [newBatchYield, setNewBatchYield] = useState<number>(0);
  const [foodCost, setFoodCost] = useState<FoodCostSummary | null>(null);
  // Copy recipe from another portion, scaled ("Half = 0.5 × Full")
  const [copySourceScope, setCopySourceScope] = useState<number | ''>('');
  const [copyFactor, setCopyFactor] = useState<string>('1');
  // Inline quantity edit on a recipe row (keyed by ingredient id within current scope)
  const [editingIngredientId, setEditingIngredientId] = useState<number | null>(null);
  const [editingQty, setEditingQty] = useState<string>('');
  // AI recipe suggestion ("magic recipe mapping")
  const [suggestRegion, setSuggestRegion] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState<RecipeSuggestion | null>(null);
  const [creatingSuggestionIdx, setCreatingSuggestionIdx] = useState<number | null>(null);

  // Audit log tab state
  const [auditLogs, setAuditLogs] = useState<StockAuditLog[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilterItem, setAuditFilterItem] = useState<number | ''>('');
  const [auditFilterType, setAuditFilterType] = useState('');
  const [auditFrom, setAuditFrom] = useState('');
  const [auditTo, setAuditTo] = useState('');
  const [auditPage, setAuditPage] = useState(0);
  const [auditRowsPerPage, setAuditRowsPerPage] = useState(15);
  const [auditTotal, setAuditTotal] = useState(0);

  // Feedback
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // --- Data fetching ---

  useEffect(() => {
    // Stewards only see the read-only Daily Sales view — vendor APIs would 403
    if (!isSteward) {
      fetchInventoryData();
      fetchItemCategories();
    }
  }, []);

  const fetchItemCategories = async () => {
    try {
      const res = await sheetsApi.getCategories();
      setItemCategories((res.data || []).filter((c) => c && c !== 'Uncategorized'));
    } catch {
      // non-critical — the category field still works as free text
    }
  };

  useEffect(() => {
    // Daily Sales also needs menu items for its entry form
    if ((activeTab === 'recipes' || activeTab === 'planner' || activeTab === 'dailySales') && menuItems.length === 0) {
      fetchMenuItems();
    }
    if (activeTab === 'audit' && auditLogs.length === 0) {
      fetchAuditLogs();
    }
  }, [activeTab]);

  const fetchInventoryData = async () => {
    try {
      setLoading(true);
      const [itemsRes, reportRes] = await Promise.all([
        inventoryApi.getItems(),
        inventoryApi.getReport(),
      ]);
      setItems(itemsRes.data || []);
      const r = reportRes.data;
      setReport({ totalItems: r.totalItems || 0, lowStockCount: r.lowStockCount || 0, outOfStockCount: r.outOfStockCount || 0 });
    } catch (err: any) {
      console.error('Error fetching inventory:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to fetch inventory data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async () => {
    if (!user?.id) return;
    try {
      const res = await api.get(`/vendors/${user.id}/menu`);
      const categories = res.data || [];
      const allItems: MenuItemOption[] = [];
      categories.forEach((cat: any) => {
        (cat.items || []).forEach((item: any) => {
          allItems.push({
            id: item.id,
            name: item.name,
            categoryName: cat.name,
            portions: (item.portions || []).map((p: { id: number; portionName: string }) => ({ id: p.id, portionName: p.portionName })),
          });
        });
      });
      setMenuItems(allItems);
    } catch (err: any) {
      console.error('Error fetching menu items:', err);
    }
  };

  const fetchRecipeForMenuItem = async (menuItemId: number) => {
    try {
      setRecipeLoading(true);
      const res = await inventoryApi.getRecipe(menuItemId);
      setRecipeMappings(res.data || []);
    } catch (err: any) {
      console.error('Error fetching recipe:', err);
      setRecipeMappings([]);
    } finally {
      setRecipeLoading(false);
    }
    fetchFoodCost(menuItemId);
  };

  const fetchFoodCost = async (menuItemId: number) => {
    try {
      const res = await inventoryApi.getFoodCost(menuItemId);
      setFoodCost(res.data || null);
    } catch (err) {
      console.error('Error fetching food cost:', err);
      setFoodCost(null);
    }
  };

  const fetchAuditLogs = async (
    itemId?: number,
    overrides?: { changeType?: string; from?: string; to?: string; page?: number; size?: number },
  ) => {
    try {
      setAuditLoading(true);
      const res = await inventoryApi.getAuditLogs({
        itemId: itemId || undefined,
        changeType: (overrides?.changeType ?? auditFilterType) || undefined,
        from: (overrides?.from ?? auditFrom) || undefined,
        to: (overrides?.to ?? auditTo) || undefined,
        page: overrides?.page ?? auditPage,
        size: overrides?.size ?? auditRowsPerPage,
      });
      // Server-side pagination: Spring Page shape
      setAuditLogs(res.data?.content || []);
      setAuditTotal(res.data?.totalElements ?? 0);
    } catch (err: any) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchInventoryData();
    if (activeTab === 'audit') await fetchAuditLogs(auditFilterItem || undefined);
    setRefreshing(false);
    setSuccess('Data refreshed successfully');
  };

  // --- Inventory item CRUD ---

  const handleOpenItemModal = (item?: InventoryItem) => {
    if (item) {
      setEditingItem(item);
      setItemForm({ name: item.name, unit: item.unit, currentStock: item.currentStock, lowStockThreshold: item.lowStockThreshold, category: item.category || '' });
    } else {
      setEditingItem(null);
      setItemForm({ name: '', unit: 'PIECES', currentStock: 0, lowStockThreshold: 5, category: '' });
    }
    setIsItemModalOpen(true);
  };

  const handleItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = { ...itemForm, category: itemForm.category?.trim() || null };
      if (editingItem) {
        await inventoryApi.updateItem(editingItem.id, payload);
        setSuccess('Item updated successfully');
      } else {
        await inventoryApi.createItem(payload);
        setSuccess('Item created successfully');
      }
      setIsItemModalOpen(false);
      setEditingItem(null);
      fetchInventoryData();
      fetchItemCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save item');
    }
  };

  const handleDeactivate = async (item: InventoryItem) => {
    const result = await confirm({
      title: 'Deactivate Item',
      message: `Are you sure you want to deactivate "${item.name}"? It will no longer appear in active inventory.`,
      confirmText: 'Deactivate',
      cancelText: 'Cancel',
      isDangerous: true,
    });
    if (result) {
      try {
        await inventoryApi.deactivateItem(item.id);
        setSuccess('Item deactivated successfully');
        fetchInventoryData();
      } catch (err: any) {
        setError(err.response?.data?.message || err.response?.data?.error || 'Failed to deactivate item');
      }
    }
  };

  // --- Stock adjustment ---

  const todayStr = () => new Date().toISOString().split('T')[0];

  const handleOpenQuickAdjust = (item: InventoryItem, mode: 'in' | 'out') => {
    setQuickAdjust({ item, mode });
    setQuickForm({ quantity: 0, occurredOn: todayStr(), reason: 'WASTAGE', notes: '' });
  };

  const handleQuickAdjustSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickAdjust || quickForm.quantity <= 0) return;
    setError(null);
    try {
      const isStockIn = quickAdjust.mode === 'in';
      await inventoryApi.adjustStock(quickAdjust.item.id, {
        quantity: isStockIn ? Math.abs(quickForm.quantity) : -Math.abs(quickForm.quantity),
        type: isStockIn ? 'RESTOCK' : quickForm.reason,
        notes: quickForm.notes || undefined,
        occurredOn: quickForm.occurredOn || undefined,
      });
      setSuccess(isStockIn ? 'Stock added' : 'Stock removed');
      setQuickAdjust(null);
      fetchInventoryData();
      if (activeTab === 'audit') fetchAuditLogs(auditFilterItem || undefined);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to adjust stock');
    }
  };

  // --- Recipe management ---

  const handleMenuItemSelect = (menuItemId: number) => {
    setSelectedMenuItemId(menuItemId);
    setRecipePortionScope('');
    setSuggestion(null);
    fetchRecipeForMenuItem(menuItemId);
  };

  const handleSuggestRecipe = async () => {
    if (!selectedMenuItemId) return;
    try {
      setSuggesting(true);
      setSuggestion(null);
      const res = await inventoryApi.suggestRecipe(selectedMenuItemId, suggestRegion.trim() || undefined);
      setSuggestion(res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'AI suggestion failed');
    } finally {
      setSuggesting(false);
    }
  };

  // Current scope: '' = base recipe, number = that portion's own recipe
  const scopePortionId = recipePortionScope === '' ? null : recipePortionScope;
  const scopedMappings = recipeMappings.filter((m) => (m.portionId ?? null) === scopePortionId);
  const selectedMenuItem = menuItems.find((mi) => mi.id === selectedMenuItemId);

  const handleAddIngredient = () => {
    if (!newIngredientId) return;
    const item = items.find((i) => i.id === newIngredientId);
    if (!item) return;
    // Avoid duplicates within the current portion scope
    if (scopedMappings.some((m) => m.inventoryItemId === newIngredientId)) {
      setError('This ingredient is already in the recipe');
      return;
    }
    // Convert sub-unit input to base unit for storage
    // e.g., 200 grams → 0.2 KG
    const unitInfo = getRecipeUnitInfo(item.unit);
    let baseQty: number;
    let batchFields: { batchQuantity?: number; batchYield?: number } = {};
    if (entryMode === 'batch') {
      if (newBatchQty <= 0 || newBatchYield <= 0) return;
      const batchBase = unitInfo.factor > 1 ? newBatchQty / unitInfo.factor : newBatchQty;
      baseQty = batchBase / newBatchYield;
      batchFields = { batchQuantity: batchBase, batchYield: newBatchYield };
    } else {
      if (newIngredientQty <= 0) return;
      baseQty = unitInfo.factor > 1 ? newIngredientQty / unitInfo.factor : newIngredientQty;
    }
    setRecipeMappings([
      ...recipeMappings,
      {
        id: 0, // temporary
        inventoryItemId: item.id,
        inventoryItemName: item.name,
        unit: item.unit,
        portionId: scopePortionId,
        portionName: scopePortionId
          ? selectedMenuItem?.portions.find((p) => p.id === scopePortionId)?.portionName || null
          : null,
        quantityRequired: baseQty,
        ...batchFields,
      },
    ]);
    setNewIngredientId('');
    setNewIngredientQty(0);
    setNewBatchQty(0);
    setNewBatchYield(0);
  };

  const handleRemoveIngredient = (mapping: RecipeMapping) => {
    setRecipeMappings(recipeMappings.filter((m) => m !== mapping));
  };

  const handleStartEditIngredient = (mapping: RecipeMapping) => {
    const unitInfo = getRecipeUnitInfo(mapping.unit);
    const displayQty = unitInfo.factor > 1
      ? Math.round(mapping.quantityRequired * unitInfo.factor * 100) / 100
      : mapping.quantityRequired;
    setEditingIngredientId(mapping.inventoryItemId);
    setEditingQty(String(displayQty));
  };

  const handleCommitEditIngredient = (mapping: RecipeMapping) => {
    const value = parseFloat(editingQty);
    setEditingIngredientId(null);
    if (!(value > 0)) return; // blank/invalid = cancel, keep old quantity
    const unitInfo = getRecipeUnitInfo(mapping.unit);
    const baseQty = unitInfo.factor > 1 ? value / unitInfo.factor : value;
    if (baseQty === mapping.quantityRequired) return;
    setRecipeMappings((prev) => prev.map((m) => (
      m.inventoryItemId === mapping.inventoryItemId && (m.portionId ?? null) === scopePortionId
        // batch provenance no longer matches an edited quantity — drop it
        ? { ...m, quantityRequired: baseQty, batchQuantity: undefined, batchYield: undefined }
        : m
    )));
  };

  // Scopes (base + portions) that already have their own rows — valid copy sources
  const copySourceOptions: { value: number | ''; label: string }[] = selectedMenuItem
    ? [
        { value: '' as const, label: 'Base recipe' },
        ...selectedMenuItem.portions.map((p) => ({ value: p.id, label: p.portionName })),
      ].filter((opt) => {
        const optScope = opt.value === '' ? null : opt.value;
        return optScope !== scopePortionId
          && recipeMappings.some((m) => (m.portionId ?? null) === optScope);
      })
    : [];

  const handleCopyScaled = () => {
    const factor = parseFloat(copyFactor);
    if (!(factor > 0)) {
      setError('Scale factor must be greater than 0');
      return;
    }
    // Mirror the Select's fallback: if the chosen source is no longer valid
    // (scope switched), use the first available option
    const chosen = copySourceOptions.some((o) => o.value === copySourceScope)
      ? copySourceScope
      : copySourceOptions[0]?.value;
    if (chosen === undefined) return;
    const sourceScope = chosen === '' ? null : chosen;
    const source = recipeMappings.filter((m) => (m.portionId ?? null) === sourceScope);
    const copies = scaleRecipeMappings(
      source,
      factor,
      scopePortionId,
      scopePortionId
        ? selectedMenuItem?.portions.find((p) => p.id === scopePortionId)?.portionName || null
        : null,
      scopedMappings,
    );
    if (copies.length === 0) {
      setError('Nothing to copy — all ingredients from that recipe are already here');
      return;
    }
    setRecipeMappings((prev) => [...prev, ...(copies as RecipeMapping[])]);
    const sourceLabel = sourceScope === null
      ? 'base recipe'
      : selectedMenuItem?.portions.find((p) => p.id === sourceScope)?.portionName || 'portion';
    setSuccess(`Copied ${copies.length} ingredient${copies.length > 1 ? 's' : ''} from ${sourceLabel} × ${factor} — review and Save Recipe`);
  };

  // Append one AI-suggested ingredient to the current draft (nothing is saved until Save Recipe)
  const buildMappingFromSuggestion = (s: SuggestedIngredient): RecipeMapping | null => {
    if (!s.matched || !s.inventoryItemId || s.convertedQuantity == null) return null;
    return {
      id: 0, // temporary
      inventoryItemId: s.inventoryItemId,
      inventoryItemName: s.inventoryItemName || s.name,
      unit: s.inventoryItemUnit || s.unit,
      portionId: scopePortionId,
      portionName: scopePortionId
        ? selectedMenuItem?.portions.find((p) => p.id === scopePortionId)?.portionName || null
        : null,
      quantityRequired: s.convertedQuantity,
    };
  };

  const handleAddSuggestion = (s: SuggestedIngredient) => {
    const mapping = buildMappingFromSuggestion(s);
    if (!mapping) return;
    if (scopedMappings.some((m) => m.inventoryItemId === mapping.inventoryItemId)) {
      setError('This ingredient is already in the recipe');
      return;
    }
    setRecipeMappings((prev) => [...prev, mapping]);
  };

  const handleAddAllSuggestions = () => {
    if (!suggestion) return;
    const existing = new Set(scopedMappings.map((m) => m.inventoryItemId));
    const additions: RecipeMapping[] = [];
    suggestion.ingredients.forEach((s) => {
      const mapping = buildMappingFromSuggestion(s);
      if (mapping && !existing.has(mapping.inventoryItemId)) {
        existing.add(mapping.inventoryItemId);
        additions.push(mapping);
      }
    });
    if (additions.length > 0) {
      setRecipeMappings((prev) => [...prev, ...additions]);
      setSuccess(`Added ${additions.length} ingredient${additions.length > 1 ? 's' : ''} to the draft — review and Save Recipe`);
    }
  };

  // Unmatched suggestion: create the inventory item (stock 0) and add it to the draft in one click
  const handleCreateAndAddSuggestion = async (s: SuggestedIngredient, idx: number) => {
    try {
      setCreatingSuggestionIdx(idx);
      const res = await inventoryApi.createItem({
        name: s.name,
        unit: s.unit,
        currentStock: 0,
        lowStockThreshold: 5,
      });
      const created = res.data;
      // Item unit = suggestion unit, so the suggested quantity needs no conversion
      setSuggestion((prev) =>
        prev
          ? {
              ...prev,
              ingredients: prev.ingredients.map((ing, i) =>
                i === idx
                  ? {
                      ...ing,
                      matched: true,
                      inventoryItemId: created.id,
                      inventoryItemName: created.name,
                      inventoryItemUnit: created.unit,
                      convertedQuantity: ing.quantityPerPortion,
                    }
                  : ing
              ),
            }
          : prev
      );
      setRecipeMappings((prev) => [
        ...prev,
        {
          id: 0, // temporary
          inventoryItemId: created.id,
          inventoryItemName: created.name,
          unit: created.unit,
          portionId: scopePortionId,
          portionName: scopePortionId
            ? selectedMenuItem?.portions.find((p) => p.id === scopePortionId)?.portionName || null
            : null,
          quantityRequired: s.quantityPerPortion,
        },
      ]);
      fetchInventoryData();
      setSuccess(`Created "${created.name}" (stock 0) and added it to the recipe draft`);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to create inventory item');
    } finally {
      setCreatingSuggestionIdx(null);
    }
  };

  const handleSaveRecipe = async () => {
    if (!selectedMenuItemId) return;
    setRecipeSaving(true);
    setError(null);
    try {
      await inventoryApi.setRecipe(selectedMenuItemId as number, {
        mappings: recipeMappings.map((m) => ({
          inventoryItemId: m.inventoryItemId,
          portionId: m.portionId ?? null,
          quantityRequired: m.quantityRequired,
          batchQuantity: m.batchQuantity ?? null,
          batchYield: m.batchYield ?? null,
        })),
      });
      setSuccess('Recipe saved successfully');
      fetchFoodCost(selectedMenuItemId as number);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save recipe');
    } finally {
      setRecipeSaving(false);
    }
  };

  // --- Filtering & pagination ---

  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const paginatedItems = filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);



  // --- Render helpers ---

  const getStockStatus = (item: InventoryItem) => {
    if (item.currentStock <= 0) return { label: 'Out of Stock', color: theme.danger || '#ef4444', bg: (theme.danger || '#ef4444') + '20' };
    if (item.isLowStock) return { label: 'Low Stock', color: theme.warning || '#d97706', bg: (theme.warning || '#d97706') + '20' };
    return { label: 'In Stock', color: theme.success || '#10b981', bg: (theme.success || '#10b981') + '20' };
  };

  const getRowHighlight = (item: InventoryItem) => {
    if (item.currentStock <= 0) return { backgroundColor: (theme.danger || '#ef4444') + '10' };
    if (item.isLowStock) return { backgroundColor: (theme.warning || '#d97706') + '10' };
    return {};
  };

  // --- Render ---

  // Stewards/cashiers get a read-only view of reported daily sales only
  if (isSteward) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh' }}>
        <Box sx={{ mb: { xs: 2, sm: 4 } }}>
          <Typography sx={{ fontWeight: 700, color: theme.text, mb: 0.5, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } }}>
            Daily Sales
          </Typography>
          <Typography variant="body2" sx={{ color: theme.gray?.[600], fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
            Reported sales days (view only)
          </Typography>
        </Box>
        <DailySalesTab menuItems={[]} theme={theme} readOnly />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Typography sx={{ fontWeight: 700, color: theme.text, mb: 0.5, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } }}>
          Inventory Management
        </Typography>
        <Typography variant="body2" sx={{ color: theme.gray?.[600], fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
          Track ingredients, manage recipes, and monitor stock levels
        </Typography>
      </Box>

      {/* Stats — slim, finance-style: value + label inline, tinted accent */}
      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, mb: { xs: 2, sm: 3 }, flexWrap: 'nowrap' }}>
        {[
          { value: report.totalItems, label: 'Total Items', color: 'var(--text-link)', icon: <CubeIcon style={{ width: 16, height: 16 }} /> },
          { value: report.lowStockCount, label: 'Low Stock', color: 'var(--status-warn-text)', icon: <ExclamationTriangleIcon style={{ width: 16, height: 16 }} /> },
          { value: report.outOfStockCount, label: 'Out of Stock', color: 'var(--status-bad)', icon: <XCircleIcon style={{ width: 16, height: 16 }} /> },
        ].map(stat => (
          <Box
            key={stat.label}
            sx={{
              flex: 1, minWidth: 0,
              display: 'flex', alignItems: 'center', gap: 1,
              px: { xs: 1.25, sm: 1.75 }, py: 1,
              backgroundColor: theme.components?.card?.background,
              border: `1px solid ${theme.components?.card?.border}`,
              borderLeft: `3px solid ${stat.color}`,
              borderRadius: '10px',
            }}
          >
            <Box sx={{ color: stat.color, display: { xs: 'none', sm: 'flex' }, flexShrink: 0 }}>{stat.icon}</Box>
            <Typography sx={{ color: theme.text, fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>{stat.value}</Typography>
            <Typography noWrap sx={{ color: theme.gray?.[600], fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>
      )}

      {/* Tabs — finance-style pills; Settings always last */}
      <Box sx={{ mb: 2 }}>
        <PillTabBar
          tabs={[
            { value: 'overview', label: 'Overview' },
            { value: 'insights', label: 'Insights' },
            { value: 'items', label: 'Inventory Items', mobileLabel: 'Items' },
            { value: 'recipes', label: 'Recipe Mappings', mobileLabel: 'Recipes' },
            { value: 'sheets', label: 'Weekly Sheets', mobileLabel: 'Sheets' },
            { value: 'planner', label: 'Planner' },
            { value: 'audit', label: 'Stock Audit Log', mobileLabel: 'Audit' },
            { value: 'variance', label: 'Variance' },
            ...(standaloneMode ? [{ value: 'dailySales', label: 'Daily Sales', mobileLabel: 'Sales' }] : []),
            { value: 'settings', label: 'Settings', mobileLabel: '⚙️' },
          ]}
          value={activeTab}
          onChange={setActiveTab}
          theme={theme}
        />
      </Box>

      {/* ========== Overview dashboard ========== */}
      {activeTab === 'overview' && (
        <InventoryOverviewTab theme={theme} />
      )}

      {/* ========== Insights (derived from stock movement data) ========== */}
      {activeTab === 'insights' && (
        <InventoryInsightsTab
          active
          onNavigate={(tab, itemId) => {
            if (tab === 'items' && itemId) {
              const target = items.find((i) => i.id === itemId);
              if (target) { setSearchTerm(target.name); setPage(0); }
            }
            setActiveTab(tab);
          }}
        />
      )}

      {/* ========== Tab 0: Inventory Items ========== */}
      {activeTab === 'items' && (
        <Paper sx={{
          backgroundColor: theme.components?.card?.background,
          border: `1px solid ${theme.components?.card?.border}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          {/* Toolbar — filters collapsed by default (finance pattern) */}
          <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: `1px solid ${theme.components?.card?.border}` }}>
            <CollapsibleFilters
              theme={theme}
              storageKey="inventory_items_filters_open"
              activeCount={searchTerm ? 1 : 0}
              onClear={() => { setSearchTerm(''); setPage(0); }}
              actions={(
                <>
                  <AppIconButton
                    onClick={handleRefresh}
                    disabled={refreshing}
                    tooltip="Refresh"
                    variant="primary"
                    size="small"
                    icon={<ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />}
                    data-testid="inventory-refresh-btn"
                  />
                  <StockCountPrintSheet theme={theme} vendorName={branding?.businessName} />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PlusIcon className="h-4 w-4" />}
                    onClick={() => handleOpenItemModal()}
                    sx={{
                      backgroundColor: `${theme.button?.secondary?.bg || theme.background} !important`,
                      color: `${theme.primary} !important`,
                      border: `1px solid ${theme.primary} !important`,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      boxShadow: 'none !important',
                      '&:hover': { backgroundColor: `${theme.button?.secondary?.hover || theme.primary} !important`, boxShadow: 'none !important' },
                    }}
                  >
                    Add Item
                  </Button>
                </>
              )}
            >
              <TextField
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                size="small"
                fullWidth
                sx={{ maxWidth: 420 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MagnifyingGlassIcon className="h-5 w-5" style={{ color: theme.gray?.[600] }} />
                    </InputAdornment>
                  ),
                }}
              />
            </CollapsibleFilters>
          </Box>

          {(loading || refreshing) && <LinearProgress />}

          {/* Table */}
          {isMobile ? (
            <Box sx={{ p: 2 }}>
              {loading && !refreshing ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={40} /></Box>
              ) : paginatedItems.length === 0 ? (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ color: theme.gray?.[600] }}>No inventory items found</Typography>
                </Box>
              ) : (
                paginatedItems.map((item) => {
                  const status = getStockStatus(item);
                  return (
                    <Card key={item.id} sx={{
                      backgroundColor: theme.components?.card?.background,
                      border: `1px solid ${theme.components?.card?.border}`,
                      borderRadius: 2,
                      mb: 2,
                    }}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: theme.text }}>{item.name}</Typography>
                              {item.isPrepared && (
                                <Chip label="Prep" size="small" sx={{ height: 18, fontSize: '0.65rem', backgroundColor: `${theme.accent}22`, color: theme.accent, fontWeight: 700 }} />
                              )}
                              {item.category && (
                                <Chip label={item.category} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', color: theme.gray?.[600], borderColor: theme.components?.card?.border, fontWeight: 600 }} />
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                              {UNITS.find((u) => u.value === item.unit)?.label || item.unit}
                            </Typography>
                          </Box>
                          <Chip label={status.label} size="small" sx={{ backgroundColor: status.bg, color: status.color, fontWeight: 600 }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>Stock</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>{item.currentStock}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>Threshold</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>{item.lowStockThreshold}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 1.5, borderTop: `1px solid ${theme.components?.card?.border}` }}>
                          <AppIconButton
                            tooltip="Stock in"
                            variant="utility"
                            size="small"
                            onClick={() => handleOpenQuickAdjust(item, 'in')}
                            icon={<PlusIcon style={{ width: 18, height: 18 }} />}
                            data-testid="stock-in-btn"
                            sx={{
                              color: theme.success, backgroundColor: `${theme.success}15`,
                              '&:hover': { backgroundColor: `${theme.success}30` },
                            }}
                          />
                          <AppIconButton
                            tooltip="Stock out"
                            variant="utility"
                            size="small"
                            onClick={() => handleOpenQuickAdjust(item, 'out')}
                            icon={<MinusIcon style={{ width: 18, height: 18 }} />}
                            data-testid="stock-out-btn"
                            sx={{
                              color: theme.warning, backgroundColor: `${theme.warning}15`,
                              '&:hover': { backgroundColor: `${theme.warning}30` },
                            }}
                          />
                          <AppIconButton
                            tooltip="Prep recipe & production"
                            variant="utility"
                            size="small"
                            onClick={() => setPrepItem(item)}
                            icon={<BeakerIcon style={{ width: 18, height: 18 }} />}
                            sx={{
                              color: theme.accent, backgroundColor: `${theme.accent}15`,
                              '&:hover': { backgroundColor: `${theme.accent}30` },
                            }}
                          />
                          <AppIconButton variant="subtle" tooltip="Edit" icon={<PencilSquareIcon style={{ width: 18, height: 18 }} />} size="small" onClick={() => handleOpenItemModal(item)} />
                          <AppIconButton variant="delete" tooltip="Deactivate" icon={<TrashIcon style={{ width: 18, height: 18 }} />} size="small" onClick={() => handleDeactivate(item)} />
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }}>Unit</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Current Stock</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Threshold</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && !refreshing ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 8 }}><CircularProgress size={40} /></TableCell></TableRow>
                  ) : paginatedItems.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" sx={{ color: theme.gray?.[600] }}>No inventory items found</Typography>
                    </TableCell></TableRow>
                  ) : (
                    paginatedItems.map((item) => {
                      const status = getStockStatus(item);
                      return (
                        <TableRow key={item.id} hover sx={getRowHighlight(item)}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: theme.text }}>{item.name}</Typography>
                              {item.isPrepared && (
                                <Chip label="Prep" size="small" sx={{ height: 18, fontSize: '0.65rem', backgroundColor: `${theme.accent}22`, color: theme.accent, fontWeight: 700 }} />
                              )}
                              {item.category && (
                                <Chip label={item.category} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', color: theme.gray?.[600], borderColor: theme.components?.card?.border, fontWeight: 600 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>
                              {UNITS.find((u) => u.value === item.unit)?.label || item.unit}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>{item.currentStock}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>{item.lowStockThreshold}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={status.label} size="small" sx={{ backgroundColor: status.bg, color: status.color, fontWeight: 600 }} />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                              <AppIconButton
                                tooltip="Stock in"
                                variant="ghost"
                                size="small"
                                onClick={() => handleOpenQuickAdjust(item, 'in')}
                                icon={<PlusIcon className="h-4 w-4" />}
                                data-testid="stock-in-btn"
                                sx={{ color: theme.success, '&:hover': { backgroundColor: `${theme.success}18`, color: theme.success } }}
                              />
                              <AppIconButton
                                tooltip="Stock out"
                                variant="ghost"
                                size="small"
                                onClick={() => handleOpenQuickAdjust(item, 'out')}
                                icon={<MinusIcon className="h-4 w-4" />}
                                data-testid="stock-out-btn"
                                sx={{ color: theme.warning, '&:hover': { backgroundColor: `${theme.warning}18`, color: theme.warning } }}
                              />
                              <AppIconButton
                                tooltip="Prep recipe & production"
                                variant="ghost"
                                size="small"
                                onClick={() => setPrepItem(item)}
                                icon={<BeakerIcon className="h-4 w-4" />}
                                sx={{ color: theme.accent, '&:hover': { backgroundColor: `${theme.accent}18`, color: theme.accent } }}
                              />
                              <AppIconButton variant="ghost" tooltip="Edit" icon={<PencilSquareIcon className="h-4 w-4" />} size="small" onClick={() => handleOpenItemModal(item)} />
                              <AppIconButton
                                variant="ghost"
                                tooltip="Deactivate"
                                icon={<TrashIcon className="h-4 w-4" />}
                                size="small"
                                onClick={() => handleDeactivate(item)}
                                sx={{ '&:hover': { backgroundColor: 'var(--status-bad-bg)', color: 'var(--status-bad)' } }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={filteredItems.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </Paper>
      )}

      {/* ========== Tab 1: Recipe Mappings ========== */}
      {activeTab === 'recipes' && (
        <Paper sx={{
          backgroundColor: theme.components?.card?.background,
          border: `1px solid ${theme.components?.card?.border}`,
          borderRadius: 2,
          p: 3,
        }}>
          {menuItems.length === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No menu items found. Create your menu first (sidebar → Menu), then come back here to map ingredients to dishes.
            </Alert>
          )}
          <Grid container spacing={3}>
            {/* Menu item selector */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Menu Item</InputLabel>
                <Select
                  value={selectedMenuItemId}
                  onChange={(e) => handleMenuItemSelect(Number(e.target.value))}
                  label="Select Menu Item"
                >
                  {menuItems.map((mi) => (
                    <MenuItem key={mi.id} value={mi.id}>
                      {mi.name} {mi.categoryName ? `(${mi.categoryName})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {!selectedMenuItemId ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CubeIcon className="h-12 w-12 mx-auto" style={{ color: theme.gray?.[400] }} />
              <Typography variant="body1" sx={{ color: theme.gray?.[600], mt: 2 }}>
                Select a menu item to view or edit its recipe
              </Typography>
            </Box>
          ) : recipeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={40} /></Box>
          ) : (
            <Box sx={{ mt: 3 }}>
              {/* Portion scope selector */}
              {selectedMenuItem && selectedMenuItem.portions.length > 0 && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="caption" sx={{ color: theme.gray?.[600], display: 'block', mb: 1 }}>
                    Recipe for
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label="Base recipe (all portions)"
                      size="small"
                      onClick={() => setRecipePortionScope('')}
                      data-testid="recipe-scope-base"
                      sx={{
                        fontWeight: 600,
                        cursor: 'pointer',
                        backgroundColor: recipePortionScope === '' ? (theme.button?.primary?.bg || theme.primary) : 'transparent',
                        color: recipePortionScope === '' ? (theme.button?.primary?.text || '#fff') : theme.text,
                        border: `1px solid ${theme.primary}`,
                      }}
                    />
                    {selectedMenuItem.portions.map((p) => (
                      <Chip
                        key={p.id}
                        label={p.portionName}
                        size="small"
                        onClick={() => setRecipePortionScope(p.id)}
                        sx={{
                          fontWeight: 600,
                          cursor: 'pointer',
                          backgroundColor: recipePortionScope === p.id ? (theme.button?.primary?.bg || theme.primary) : 'transparent',
                          color: recipePortionScope === p.id ? (theme.button?.primary?.text || '#fff') : theme.text,
                          border: `1px solid ${theme.primary}`,
                        }}
                      />
                    ))}
                  </Box>
                  {/* Copy another portion's recipe, scaled (Half = 0.5 × Full) */}
                  {copySourceOptions.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1.5 }}>
                      <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                        Copy from
                      </Typography>
                      <Select
                        size="small"
                        value={copySourceOptions.some((o) => o.value === copySourceScope) ? copySourceScope : copySourceOptions[0].value}
                        onChange={(e) => setCopySourceScope(e.target.value === '' ? '' : Number(e.target.value))}
                        data-testid="copy-recipe-source"
                        sx={{ minWidth: 140, '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' } }}
                      >
                        {copySourceOptions.map((opt) => (
                          <MenuItem key={opt.value === '' ? 'base' : opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>×</Typography>
                      <TextField
                        size="small"
                        value={copyFactor}
                        onChange={(e) => setCopyFactor(e.target.value)}
                        inputProps={{ inputMode: 'decimal', style: { padding: '4px 8px', fontSize: '0.8rem', width: 48, textAlign: 'center' } }}
                        data-testid="copy-recipe-factor"
                      />
                      {[0.5, 1, 1.5, 2].map((f) => (
                        <Chip
                          key={f}
                          label={`×${f}`}
                          size="small"
                          onClick={() => setCopyFactor(String(f))}
                          sx={{
                            cursor: 'pointer',
                            height: 22,
                            fontSize: '0.7rem',
                            backgroundColor: copyFactor === String(f) ? (theme.button?.primary?.bg || theme.primary) : 'transparent',
                            color: copyFactor === String(f) ? (theme.button?.primary?.text || '#fff') : theme.text,
                            border: `1px solid ${theme.gray?.[300] || '#ccc'}`,
                          }}
                        />
                      ))}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleCopyScaled}
                        data-testid="copy-recipe-btn"
                        sx={{ textTransform: 'none', fontWeight: 600, py: 0.25 }}
                      >
                        Copy &amp; scale
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* AI recipe suggestion ("magic recipe mapping") */}
              <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: `1px dashed ${theme.primary}` }}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Region / style (optional)"
                    placeholder="e.g. Hyderabad"
                    value={suggestRegion}
                    onChange={(e) => setSuggestRegion(e.target.value)}
                    sx={{ minWidth: 200 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleSuggestRecipe}
                    disabled={suggesting}
                    startIcon={suggesting ? <CircularProgress size={16} /> : <SparklesIcon className="h-4 w-4" />}
                    data-testid="suggest-recipe-btn"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {suggesting ? 'Suggesting…' : 'Magic recipe mapping'}
                  </Button>
                  {suggestion && suggestion.ingredients.some((s) => s.matched && s.convertedQuantity != null) && (
                    <Button
                      size="small"
                      onClick={handleAddAllSuggestions}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                      data-testid="suggest-add-all-btn"
                    >
                      Add all matched
                    </Button>
                  )}
                  {suggestion && (
                    <Button
                      size="small"
                      onClick={() => setSuggestion(null)}
                      sx={{ textTransform: 'none', color: theme.gray?.[500], ml: 'auto' }}
                    >
                      Dismiss
                    </Button>
                  )}
                </Box>
                {suggesting && (
                  <Typography variant="caption" sx={{ color: theme.gray?.[500], display: 'block', mt: 1 }}>
                    Asking the chef AI for a {suggestRegion.trim() ? `${suggestRegion.trim()}-style ` : ''}recipe — usually takes 10–20 seconds…
                  </Typography>
                )}
                {suggestion && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      AI estimate ({suggestion.model}) — use as a guide only. Verify quantities against your kitchen before saving.
                    </Alert>
                    <Typography variant="caption" sx={{ color: theme.gray?.[600], display: 'block', mb: 1 }}>
                      {suggestion.ingredients.filter((s) => s.matched).length} of {suggestion.ingredients.length} ingredients
                      matched your inventory{suggestion.region ? ` · ${suggestion.region} style` : ''}
                    </Typography>
                    {(suggestion.servingDescription || suggestion.servingWeightGrams) && (
                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.75, mb: 1, px: 1.25, py: 0.5,
                        borderRadius: 1.5, backgroundColor: `${theme.success}12`, border: `1px solid ${theme.success}55`,
                      }} data-testid="suggestion-serving">
                        <Typography variant="caption" sx={{ fontWeight: 700, color: theme.text }}>
                          End product per portion:
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.text }}>
                          {suggestion.servingDescription
                            || `~${formatQuantity(suggestion.servingWeightGrams || 0, 'GRAMS')}`}
                          {suggestion.servingDescription && suggestion.servingWeightGrams
                            && !suggestion.servingDescription.includes(String(Math.round(suggestion.servingWeightGrams)))
                            ? ` (~${formatQuantity(suggestion.servingWeightGrams, 'GRAMS')})`
                            : ''}
                        </Typography>
                      </Box>
                    )}
                    {suggestion.ingredients.map((s, idx) => {
                      const alreadyAdded = s.matched && scopedMappings.some((m) => m.inventoryItemId === s.inventoryItemId);
                      return (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            py: 0.75,
                            borderBottom: `1px solid ${theme.gray?.[200] || '#eee'}`,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text, minWidth: 140 }}>
                            {s.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>
                            {s.quantityPerPortion} {s.unit} / portion{s.notes ? ` · ${s.notes}` : ''}
                          </Typography>
                          {s.matched ? (
                            <>
                              <Chip size="small" variant="outlined" color="success" label={`→ ${s.inventoryItemName}`} />
                              {s.convertedQuantity == null && (
                                <Chip size="small" variant="outlined" color="warning" label="unit mismatch — add manually" />
                              )}
                              <Button
                                size="small"
                                disabled={s.convertedQuantity == null || alreadyAdded}
                                onClick={() => handleAddSuggestion(s)}
                                sx={{ ml: 'auto', textTransform: 'none', fontWeight: 600 }}
                              >
                                {alreadyAdded ? 'Added' : 'Add'}
                              </Button>
                            </>
                          ) : (
                            <>
                              <Chip size="small" variant="outlined" color="warning" label="not in inventory" />
                              <Button
                                size="small"
                                disabled={creatingSuggestionIdx !== null}
                                onClick={() => handleCreateAndAddSuggestion(s, idx)}
                                startIcon={creatingSuggestionIdx === idx ? <CircularProgress size={12} /> : undefined}
                                sx={{ ml: 'auto', textTransform: 'none', fontWeight: 600 }}
                              >
                                Create &amp; add
                              </Button>
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              {/* Current recipe */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text, mb: 2 }}>
                Recipe Ingredients
              </Typography>

              {scopedMappings.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {scopePortionId
                    ? 'This portion has no ingredients of its own yet — it currently uses the base recipe. Add ingredients below to give it a portion-specific recipe.'
                    : 'No ingredients mapped yet. Add ingredients below.'}
                </Alert>
              ) : isMobile ? (
                <Box sx={{ mb: 3 }}>
                  {scopedMappings.map((mapping) => {
                    const unitInfo = getRecipeUnitInfo(mapping.unit);
                    const displayQty = unitInfo.factor > 1
                      ? Math.round(mapping.quantityRequired * unitInfo.factor * 100) / 100
                      : mapping.quantityRequired;
                    return (
                      <Card key={mapping.inventoryItemId} sx={{
                        backgroundColor: theme.components?.card?.background,
                        border: `1px solid ${theme.components?.card?.border}`,
                        borderRadius: 2,
                        mb: 1.5,
                      }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                                {mapping.inventoryItemName}
                              </Typography>
                              {editingIngredientId === mapping.inventoryItemId ? (
                                <TextField
                                  size="small"
                                  autoFocus
                                  value={editingQty}
                                  onChange={(e) => setEditingQty(e.target.value)}
                                  onBlur={() => handleCommitEditIngredient(mapping)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleCommitEditIngredient(mapping);
                                    if (e.key === 'Escape') setEditingIngredientId(null);
                                  }}
                                  inputProps={{ inputMode: 'decimal', style: { padding: '4px 8px', fontSize: '0.85rem', width: 72 } }}
                                  InputProps={{ endAdornment: <InputAdornment position="end">{unitInfo.label}</InputAdornment> }}
                                />
                              ) : (
                                <Typography variant="caption" sx={{ color: theme.gray?.[600] }} onClick={() => handleStartEditIngredient(mapping)}>
                                  {displayQty} {unitInfo.label}
                                  {mapping.batchQuantity && mapping.batchYield
                                    ? ` · batch: ${Math.round(mapping.batchQuantity * (unitInfo.factor > 1 ? unitInfo.factor : 1) * 100) / 100} ${unitInfo.label} / ${mapping.batchYield} portions`
                                    : ''}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <AppIconButton variant="subtle" tooltip="Edit quantity" icon={<PencilSquareIcon style={{ width: 16, height: 16 }} />} size="small" onClick={() => handleStartEditIngredient(mapping)} />
                              <AppIconButton variant="ghost" tooltip="Remove" icon={<TrashIcon style={{ width: 16, height: 16 }} />} size="small" onClick={() => handleRemoveIngredient(mapping)} sx={{ '&:hover': { backgroundColor: 'var(--status-bad-bg)', color: 'var(--status-bad)' } }} />
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              ) : (
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: theme.text }}>Ingredient</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.text }}>Unit</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Quantity</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scopedMappings.map((mapping) => {
                        const unitInfo = getRecipeUnitInfo(mapping.unit);
                        const displayQty = unitInfo.factor > 1
                          ? Math.round(mapping.quantityRequired * unitInfo.factor * 100) / 100
                          : mapping.quantityRequired;
                        return (
                        <TableRow key={`${mapping.inventoryItemId}-${mapping.portionId ?? 'base'}`}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.text }}>
                              {mapping.inventoryItemName}
                            </Typography>
                            {mapping.batchQuantity && mapping.batchYield ? (
                              <Typography variant="caption" sx={{ color: theme.gray?.[500], display: 'block' }}>
                                batch: {Math.round(mapping.batchQuantity * (unitInfo.factor > 1 ? unitInfo.factor : 1) * 100) / 100} {unitInfo.label} / {mapping.batchYield} portions
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>
                              {unitInfo.label}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {editingIngredientId === mapping.inventoryItemId ? (
                              <TextField
                                size="small"
                                autoFocus
                                value={editingQty}
                                onChange={(e) => setEditingQty(e.target.value)}
                                onBlur={() => handleCommitEditIngredient(mapping)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleCommitEditIngredient(mapping);
                                  if (e.key === 'Escape') setEditingIngredientId(null);
                                }}
                                inputProps={{ inputMode: 'decimal', style: { padding: '4px 8px', fontSize: '0.85rem', width: 64, textAlign: 'right' } }}
                                data-testid="recipe-qty-edit"
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                onClick={() => handleStartEditIngredient(mapping)}
                                sx={{ fontWeight: 600, color: theme.text, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                              >
                                {displayQty}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                              <AppIconButton variant="ghost" tooltip="Edit quantity" icon={<PencilSquareIcon style={{ width: 16, height: 16 }} />} size="small" onClick={() => handleStartEditIngredient(mapping)} data-testid="recipe-qty-edit-btn" />
                              <AppIconButton variant="ghost" tooltip="Remove" icon={<TrashIcon style={{ width: 16, height: 16 }} />} size="small" onClick={() => handleRemoveIngredient(mapping)} sx={{ '&:hover': { backgroundColor: 'var(--status-bad-bg)', color: 'var(--status-bad)' } }} />
                            </Box>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Add ingredient row */}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.text }}>
                  Add Ingredient
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {([
                    { value: 'portion', label: 'Per portion' },
                    { value: 'batch', label: 'Per batch' },
                  ] as const).map((mode) => (
                    <Chip
                      key={mode.value}
                      label={mode.label}
                      size="small"
                      onClick={() => setEntryMode(mode.value)}
                      data-testid={`entry-mode-${mode.value}`}
                      sx={{
                        fontWeight: 600,
                        cursor: 'pointer',
                        backgroundColor: entryMode === mode.value ? (theme.button?.primary?.bg || theme.primary) : 'transparent',
                        color: entryMode === mode.value ? (theme.button?.primary?.text || '#fff') : theme.text,
                        border: `1px solid ${theme.primary}`,
                      }}
                    />
                  ))}
                </Box>
              </Box>
              {entryMode === 'batch' && (
                <Typography variant="caption" sx={{ color: theme.gray?.[600], display: 'block', mb: 1.5 }}>
                  Enter how much of the ingredient goes into one batch and how many portions the batch yields — e.g. 3 KG rice makes 80 idlis.
                </Typography>
              )}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={entryMode === 'batch' ? 4 : 5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Ingredient</InputLabel>
                    <Select
                      value={newIngredientId}
                      onChange={(e) => setNewIngredientId(Number(e.target.value))}
                      label="Ingredient"
                    >
                      {items
                        .filter((i) => !scopedMappings.some((m) => m.inventoryItemId === i.id))
                        .map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name} ({UNITS.find((u) => u.value === item.unit)?.label || item.unit})
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                {entryMode === 'portion' ? (
                  <Grid item xs={6} sm={3}>
                    <TextField
                      label={(() => {
                        if (!newIngredientId) return 'Qty per portion';
                        const sel = items.find((i) => i.id === newIngredientId);
                        if (!sel) return 'Qty per portion';
                        const info = getRecipeUnitInfo(sel.unit);
                        return `Qty (${info.label})`;
                      })()}
                      type="number"
                      size="small"
                      fullWidth
                      value={newIngredientQty || ''}
                      onChange={(e) => setNewIngredientQty(parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label={(() => {
                          const sel = items.find((i) => i.id === newIngredientId);
                          const info = sel ? getRecipeUnitInfo(sel.unit) : null;
                          return info ? `Batch qty (${info.label})` : 'Batch qty';
                        })()}
                        type="number"
                        size="small"
                        fullWidth
                        value={newBatchQty || ''}
                        onChange={(e) => setNewBatchQty(parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        label="Yields (portions)"
                        type="number"
                        size="small"
                        fullWidth
                        value={newBatchYield || ''}
                        onChange={(e) => setNewBatchYield(parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={6} sm={3}>
                  <Button
                    variant="contained"
                    startIcon={<PlusIcon className="h-4 w-4" />}
                    onClick={handleAddIngredient}
                    disabled={
                      !newIngredientId ||
                      (entryMode === 'portion' ? newIngredientQty <= 0 : newBatchQty <= 0 || newBatchYield <= 0)
                    }
                    sx={{
                      backgroundColor: `${theme.button?.secondary?.bg || theme.background} !important`,
                      color: `${theme.primary} !important`,
                      border: `1px solid ${theme.primary} !important`,
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: 'none !important',
                      '&:hover': { backgroundColor: `${theme.button?.secondary?.hover || theme.primary} !important`, boxShadow: 'none !important' },
                      '&:disabled': { opacity: 0.5 },
                    }}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>

              {/* Save recipe */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSaveRecipe}
                  disabled={recipeSaving}
                  sx={{
                    backgroundColor: `${theme.accent}22 !important`,
                    color: `${theme.accent} !important`,
                    border: `1px solid ${theme.accent} !important`,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: 'none !important',
                    '&:hover': { backgroundColor: `${theme.accent}44 !important`, boxShadow: 'none !important' },
                  }}
                >
                  {recipeSaving ? <CircularProgress size={20} /> : 'Save Recipe'}
                </Button>
              </Box>

              {/* Food cost breakdown */}
              {foodCost && (foodCost.foodCost > 0 || foodCost.portions.some((p) => p.foodCost > 0)) && (
                <Box sx={{ mt: 4 }} data-testid="food-cost-card">
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text, mb: 0.5 }}>
                    Food Cost
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.gray?.[600], display: 'block', mb: 2 }}>
                    Ingredient cost per serving vs selling price, based on current ingredient costs. Save the recipe to refresh.
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, color: theme.text }}>Portion</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Price</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Cost</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Margin</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[
                          {
                            key: 'base',
                            name: foodCost.portions.length > 0 ? 'Standard' : foodCost.menuItemName,
                            price: foodCost.sellingPrice,
                            cost: foodCost.foodCost,
                            margin: foodCost.marginPercent,
                            usesBase: false,
                          },
                          ...foodCost.portions.map((p) => ({
                            key: `p-${p.portionId}`,
                            name: p.portionName,
                            price: p.sellingPrice,
                            cost: p.foodCost,
                            margin: p.marginPercent,
                            usesBase: p.usesBaseRecipe,
                          })),
                        ].map((row) => (
                          <TableRow key={row.key}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: theme.text }}>
                                {row.name}
                                {row.usesBase && (
                                  <Typography component="span" variant="caption" sx={{ color: theme.gray?.[500], ml: 0.75 }}>
                                    (base recipe)
                                  </Typography>
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ color: theme.text }}>
                                {row.price != null ? formatCurrency(row.price) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ color: theme.text }}>
                                {row.cost > 0 ? formatCurrency(row.cost) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {row.margin != null ? (
                                <Typography variant="body2" sx={{
                                  fontWeight: 700,
                                  color: row.margin >= 65 ? (theme.success || '#10b981')
                                    : row.margin >= 45 ? (theme.warning || '#d97706')
                                    : (theme.danger || '#ef4444'),
                                }}>
                                  {Math.round(row.margin * 10) / 10}%
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ color: theme.gray?.[500] }}>—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* ========== Weekly paper sheets (download + photo import) ========== */}
      {activeTab === 'sheets' && (
        <WeeklySheetsTab theme={theme} vendorId={user?.id} />
      )}

      {/* ========== Tab 2: Planner ========== */}
      {activeTab === 'planner' && (
        <PlannerTab menuItems={menuItems} theme={theme} />
      )}

      {/* ========== Tab 3: Stock Audit Log ========== */}
      {activeTab === 'audit' && (
        <Paper sx={{
          backgroundColor: theme.components?.card?.background,
          border: `1px solid ${theme.components?.card?.border}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          {/* Filter toolbar — collapsed by default (finance pattern) */}
          <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: `1px solid ${theme.components?.card?.border}` }}>
            <CollapsibleFilters
              theme={theme}
              storageKey="inventory_audit_filters_open"
              activeCount={(auditFilterItem ? 1 : 0) + (auditFilterType ? 1 : 0) + (auditFrom ? 1 : 0) + (auditTo ? 1 : 0)}
              onClear={() => {
                setAuditFilterItem(''); setAuditFilterType(''); setAuditFrom(''); setAuditTo('');
                setAuditPage(0);
                fetchAuditLogs(undefined, { changeType: '', from: '', to: '', page: 0 });
              }}
              actions={(
                <AppIconButton
                  onClick={() => fetchAuditLogs(auditFilterItem || undefined)}
                  disabled={auditLoading}
                  tooltip="Refresh"
                  variant="primary"
                  size="small"
                  icon={<ArrowPathIcon className={`h-4 w-4 ${auditLoading ? 'animate-spin' : ''}`} />}
                  data-testid="audit-refresh-btn"
                />
              )}
            >
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 180, flex: 1, maxWidth: 260 }}>
                  <InputLabel>Item</InputLabel>
                  <Select
                    value={auditFilterItem}
                    onChange={(e) => {
                      const val = e.target.value as number | '';
                      setAuditFilterItem(val);
                      setAuditPage(0);
                      fetchAuditLogs(val || undefined, { page: 0 });
                    }}
                    label="Item"
                  >
                    <MenuItem value="">All Items</MenuItem>
                    {items.map((item) => (
                      <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 170, flex: 1, maxWidth: 240 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={auditFilterType}
                    onChange={(e) => {
                      const val = e.target.value as string;
                      setAuditFilterType(val);
                      setAuditPage(0);
                      fetchAuditLogs(auditFilterItem || undefined, { changeType: val, page: 0 });
                    }}
                    label="Type"
                    data-testid="audit-type-filter"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="RESTOCK">Purchases (Restock)</MenuItem>
                    <MenuItem value="WASTAGE">Wastage</MenuItem>
                    <MenuItem value="KITCHEN_USE">Kitchen use</MenuItem>
                    <MenuItem value="MANUAL_ADJUSTMENT">Correction</MenuItem>
                    <MenuItem value="RETURNED_TO_SUPPLIER">Returned to supplier</MenuItem>
                    <MenuItem value="ORDER_DEDUCTION">Order deduction</MenuItem>
                    <MenuItem value="MANUAL_SALE">Manual sale</MenuItem>
                    <MenuItem value="PREP_PRODUCTION">Prep production</MenuItem>
                    <MenuItem value="PREP_CONSUMPTION">Prep consumption</MenuItem>
                    <MenuItem value="STOCK_COUNT_ADJUSTMENT">Stock count</MenuItem>
                    <MenuItem value="INITIAL_STOCK">Initial stock</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={auditFrom}
                  onChange={(e) => {
                    setAuditFrom(e.target.value);
                    setAuditPage(0);
                    fetchAuditLogs(auditFilterItem || undefined, { from: e.target.value, page: 0 });
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 'data-testid': 'audit-from-date' }}
                  sx={{ width: 160 }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={auditTo}
                  onChange={(e) => {
                    setAuditTo(e.target.value);
                    setAuditPage(0);
                    fetchAuditLogs(auditFilterItem || undefined, { to: e.target.value, page: 0 });
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 'data-testid': 'audit-to-date' }}
                  sx={{ width: 160 }}
                />
              </Box>
            </CollapsibleFilters>
          </Box>

          {auditLoading && <LinearProgress />}

          {isMobile ? (
            <Box sx={{ p: 2 }}>
              {auditLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={40} /></Box>
              ) : auditLogs.length === 0 ? (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ color: theme.gray?.[600] }}>No audit logs found</Typography>
                </Box>
              ) : (
                auditLogs.map((log) => {
                  const typeColor = CHANGE_TYPE_COLORS[log.changeType] || CHANGE_TYPE_COLORS.MANUAL_ADJUSTMENT;
                  return (
                    <Card key={log.id} sx={{
                      backgroundColor: theme.components?.card?.background,
                      border: `1px solid ${theme.components?.card?.border}`,
                      borderRadius: 2,
                      mb: 1.5,
                    }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>{log.inventoryItemName}</Typography>
                            <Typography variant="caption" sx={{ color: theme.gray?.[500] }}>
                              {new Date(log.occurredOn || log.createdAt).toLocaleDateString()}{' '}
                              {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Box>
                          <Chip
                            label={log.changeType.replace(/_/g, ' ')}
                            size="small"
                            sx={{ backgroundColor: typeColor.bg, color: typeColor.color, fontWeight: 600, fontSize: '0.7rem' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>Change</Typography>
                            <Typography variant="body2" sx={{
                              fontWeight: 600,
                              color: log.quantityChange > 0 ? (theme.success || '#10b981') : (theme.danger || '#ef4444'),
                            }}>
                              {log.quantityChange > 0 ? '+' : ''}{log.quantityChange}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>Before</Typography>
                            <Typography variant="body2" sx={{ color: theme.text }}>{log.stockBefore}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>After</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.text }}>{log.stockAfter}</Typography>
                          </Box>
                          {log.orderId && (
                            <Box>
                              <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>Order</Typography>
                              <Typography variant="body2" sx={{ color: theme.text }}>#{log.orderId}</Typography>
                            </Box>
                          )}
                        </Box>
                        {log.notes && (
                          <Typography variant="caption" sx={{ color: theme.gray?.[500], mt: 0.5, display: 'block' }}>
                            {log.notes}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }}>Change Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Qty Change</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Before</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">After</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }}>Order #</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.text }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLoading ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 8 }}><CircularProgress size={40} /></TableCell></TableRow>
                  ) : auditLogs.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" sx={{ color: theme.gray?.[600] }}>No audit logs found</Typography>
                    </TableCell></TableRow>
                  ) : (
                    auditLogs.map((log) => {
                      const typeColor = CHANGE_TYPE_COLORS[log.changeType] || CHANGE_TYPE_COLORS.MANUAL_ADJUSTMENT;
                      return (
                        <TableRow key={log.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: theme.text }}>
                              {new Date(log.occurredOn || log.createdAt).toLocaleDateString()}{' '}
                              <Typography component="span" variant="caption" sx={{ color: theme.gray?.[500] }}>
                                {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.text }}>{log.inventoryItemName}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={log.changeType.replace(/_/g, ' ')}
                              size="small"
                              sx={{ backgroundColor: typeColor.bg, color: typeColor.color, fontWeight: 600, fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: log.quantityChange > 0 ? (theme.success || '#10b981') : (theme.danger || '#ef4444'),
                              }}
                            >
                              {log.quantityChange > 0 ? '+' : ''}{log.quantityChange}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>{log.stockBefore}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.text }}>{log.stockAfter}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>{log.orderId || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: theme.gray?.[600], maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {log.notes || '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={auditTotal}
            page={auditPage}
            onPageChange={(_, newPage) => { setAuditPage(newPage); fetchAuditLogs(auditFilterItem || undefined, { page: newPage }); }}
            rowsPerPage={auditRowsPerPage}
            onRowsPerPageChange={(e) => {
              const size = parseInt(e.target.value, 10);
              setAuditRowsPerPage(size); setAuditPage(0);
              fetchAuditLogs(auditFilterItem || undefined, { page: 0, size });
            }}
          />
        </Paper>
      )}

      {/* ========== Tab 4: Consumption Variance ========== */}
      {activeTab === 'variance' && (
        <VarianceReportTab theme={theme} initialRange={varianceRange} />
      )}

      {/* ========== Tab 5: Inventory Settings ========== */}
      {activeTab === 'settings' && (
        <Paper sx={{
          backgroundColor: theme.components?.card?.background,
          border: `1px solid ${theme.components?.card?.border}`,
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
          maxWidth: 720,
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text, mb: 0.5 }}>
            Inventory Settings
          </Typography>
          <Typography variant="body2" sx={{ color: theme.gray?.[600], mb: 2 }}>
            How sales reach your stock — through SOG orders or manual daily reports
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 2, borderBottom: `1px solid ${theme.components?.card?.border}` }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>POS-integrated Sales</Typography>
              <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                ON: orders placed in SOG deduct stock and feed the planner.
                OFF: standalone mode — report what sold each day in the Daily Sales tab.
              </Typography>
            </Box>
            <ToggleSwitch
              enabled={configuration?.inventoryPosIntegrated ?? true}
              onChange={() => handleToggleInventorySetting('inventoryPosIntegrated', configuration?.inventoryPosIntegrated ?? true)}
              disabled={configSaving}
              color={theme.primary}
              size="sm"
            />
          </Box>

          {standaloneMode && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>Deduct Stock on Manual Sales</Typography>
                <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                  Reported daily sales deduct ingredients through recipes. Shortfalls are flagged, never blocked.
                </Typography>
              </Box>
              <ToggleSwitch
                enabled={configuration?.inventoryDeductOnManualSales ?? true}
                onChange={() => handleToggleInventorySetting('inventoryDeductOnManualSales', configuration?.inventoryDeductOnManualSales ?? true)}
                disabled={configSaving}
                color={theme.primary}
                size="sm"
              />
            </Box>
          )}
        </Paper>
      )}

      {/* ========== Tab 6: Daily Sales (standalone mode) ========== */}
      {standaloneMode && activeTab === 'dailySales' && (
        <DailySalesTab
          menuItems={menuItems}
          theme={theme}
          onViewVariance={(date) => {
            setVarianceRange({ from: date, to: date });
            setActiveTab('variance');
          }}
        />
      )}

      {/* ========== Prep Recipe Dialog ========== */}
      <PrepRecipeDialog
        open={prepItem !== null}
        onClose={() => setPrepItem(null)}
        item={prepItem ? { id: prepItem.id, name: prepItem.name, unit: prepItem.unit } : null}
        inventoryItems={items.map((i) => ({ id: i.id, name: i.name, unit: i.unit, currentStock: i.currentStock }))}
        theme={theme}
        onChanged={fetchInventoryData}
      />

      {/* ========== Create/Edit Item Modal ========== */}
      <Modal
        isOpen={isItemModalOpen}
        onClose={() => { setIsItemModalOpen(false); setEditingItem(null); setError(null); }}
        title={editingItem ? 'Edit Inventory Item' : 'Add Inventory Item'}
        maxWidth="md"
      >
        <form onSubmit={handleItemSubmit}>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Item Name"
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                required
                fullWidth
                placeholder="e.g., Tomatoes, Chicken, Rice"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={itemCategories}
                value={itemForm.category}
                onChange={(_, val) => setItemForm({ ...itemForm, category: val || '' })}
                onInputChange={(_, val) => setItemForm({ ...itemForm, category: val })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category (optional)"
                    placeholder="e.g., Kitchen, Bar, Dry store"
                    helperText="Groups the item on weekly stock sheets"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Unit of Measure</InputLabel>
                <Select
                  value={itemForm.unit}
                  onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
                  label="Unit of Measure"
                >
                  {UNITS.map((u) => (
                    <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Current Stock"
                type="number"
                value={itemForm.currentStock}
                onChange={(e) => setItemForm({ ...itemForm, currentStock: parseFloat(e.target.value) || 0 })}
                required
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Low Stock Threshold"
                type="number"
                value={itemForm.lowStockThreshold}
                onChange={(e) => setItemForm({ ...itemForm, lowStockThreshold: parseFloat(e.target.value) || 0 })}
                required
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={() => { setIsItemModalOpen(false); setEditingItem(null); }}
              variant="outlined"
              sx={{
                backgroundColor: `${theme.button?.secondary?.bg || theme.background} !important`,
                color: `${theme.primary} !important`,
                border: `1px solid ${theme.primary} !important`,
                fontWeight: 500,
                '&:hover': { backgroundColor: `${theme.button?.secondary?.hover || theme.primary} !important`, borderColor: `${theme.primary} !important` },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: `${theme.accent}22 !important`,
                color: `${theme.accent} !important`,
                border: `1px solid ${theme.accent} !important`,
                fontWeight: 500,
                boxShadow: 'none !important',
                '&:hover': { backgroundColor: `${theme.accent}44 !important`, boxShadow: 'none !important' },
              }}
            >
              {editingItem ? 'Update' : 'Create'} Item
            </Button>
          </Box>
        </form>
      </Modal>

      {/* ========== Quick Stock In/Out Modal ========== */}
      <Modal
        isOpen={quickAdjust !== null}
        onClose={() => { setQuickAdjust(null); setError(null); }}
        title={quickAdjust ? `${quickAdjust.mode === 'in' ? 'Stock in' : 'Stock out'}: ${quickAdjust.item.name}` : ''}
        maxWidth="sm"
      >
        {quickAdjust && (
          <form onSubmit={handleQuickAdjustSubmit}>
            <Typography variant="body2" sx={{ color: theme.gray?.[600], mb: 2 }}>
              Current stock:{' '}
              <strong style={{ color: theme.text }}>
                {quickAdjust.item.currentStock} {UNITS.find((u) => u.value === quickAdjust.item.unit)?.label || quickAdjust.item.unit}
              </strong>
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={7}>
                <TextField
                  label={`Quantity (${UNITS.find((u) => u.value === quickAdjust.item.unit)?.label || quickAdjust.item.unit})`}
                  type="number"
                  value={quickForm.quantity || ''}
                  onChange={(e) => setQuickForm({ ...quickForm, quantity: parseFloat(e.target.value) || 0 })}
                  required
                  fullWidth
                  size="small"
                  autoFocus
                  inputProps={{ min: 0.01, step: 0.01, 'data-testid': 'quick-adjust-qty' }}
                />
              </Grid>
              <Grid item xs={5}>
                <TextField
                  label="Date"
                  type="date"
                  value={quickForm.occurredOn}
                  onChange={(e) => setQuickForm({ ...quickForm, occurredOn: e.target.value })}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: todayStr(), 'data-testid': 'quick-adjust-date' }}
                  helperText="When it happened"
                />
              </Grid>

              {quickAdjust.mode === 'out' && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: theme.gray?.[600], fontWeight: 600, display: 'block', mb: 0.75 }}>
                    Reason
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {STOCK_OUT_REASONS.map((r) => (
                      <Chip
                        key={r.value}
                        label={r.label}
                        size="small"
                        onClick={() => setQuickForm({ ...quickForm, reason: r.value })}
                        data-testid={`stock-out-reason-${r.value}`}
                        sx={{
                          fontWeight: 600,
                          cursor: 'pointer',
                          backgroundColor: quickForm.reason === r.value ? theme.primary : `${theme.gray?.[200]}55`,
                          color: quickForm.reason === r.value ? '#FFFFFF' : theme.gray?.[600],
                          '&:hover': { backgroundColor: quickForm.reason === r.value ? theme.primary : `${theme.gray?.[200]}AA` },
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={quickForm.notes}
                  onChange={(e) => setQuickForm({ ...quickForm, notes: e.target.value })}
                  fullWidth
                  size="small"
                  placeholder="Optional"
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => setQuickAdjust(null)}
                size="small"
                sx={{ color: theme.gray?.[600], textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={!quickForm.quantity || quickForm.quantity <= 0}
                data-testid="quick-adjust-submit"
                sx={{
                  backgroundColor: `${quickAdjust.mode === 'in' ? theme.success : theme.warning}22 !important`,
                  color: `${quickAdjust.mode === 'in' ? theme.success : theme.warning} !important`,
                  border: `1px solid ${quickAdjust.mode === 'in' ? theme.success : theme.warning} !important`,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none !important',
                  '&:disabled': { opacity: 0.5 },
                }}
              >
                {quickAdjust.mode === 'in' ? 'Add stock' : 'Remove stock'}
              </Button>
            </Box>
          </form>
        )}
      </Modal>
    </Box>
  );
};

export default InventoryManagement;
